import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaStar, FaQuoteLeft, FaCalendarAlt, FaThumbsUp } from 'react-icons/fa';
import axios from 'axios';
import ReviewForm from '../components/ReviewForm';
import { staticReviews } from '../config/staticData';
import { companyInfo, organizationSchema } from '../config/seoConfig';
import { pageSeoData } from '../config/pageSeoConfig';
import './Reviews.scss';

interface Review {
  id: number;
  name: string;
  company: string;
  position: string;
  rating: number;
  review: string;
  image?: string;
  approved: boolean;
  createdAt: string;
  updatedAt: string;
}

const Reviews: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Client Reviews - What Our Clients Say | App Developer Pro';
  }, []);

  useEffect(() => {
    // Use static data instead of API
    setReviews(staticReviews);
    setLoading(false);
  }, []);

  // SEO constants from config
  const seoTitle = pageSeoData.reviews.title;
  const seoDescription = pageSeoData.reviews.description;
  const seoKeywords = pageSeoData.reviews.keywords;
  const seoUrl = pageSeoData.reviews.url;
  const seoImage = "https://appdeveloperpro.com/images/client-reviews-testimonials.jpg";

  // SEO Meta Tags and Schema Markup

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <FaStar
        key={index}
        className={`reviews__star ${index < rating ? 'reviews__star--filled' : ''}`}
      />
    ));
  };

  const averageRating = reviews.length > 0 
    ? reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length 
    : 5.0;
  const totalReviews = reviews.length;

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <link rel="canonical" href={seoUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:image" content={seoImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Client Reviews and Testimonials - App Developer Pro" />
        <meta property="og:site_name" content="App Developer Pro" />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoImage} />
        <meta name="twitter:image:alt" content="Client Reviews and Testimonials - App Developer Pro" />
        <meta name="twitter:site" content="@AppDevPro" />
        <meta name="twitter:creator" content="@AppDevPro" />
        <meta name="author" content="App Developer Pro" />
        <meta name="publisher" content="App Developer Pro" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="theme-color" content="#3B82F6" />
        <meta name="format-detection" content="telephone=no" />
        <meta property="business:contact_data:street_address" content={companyInfo.address.streetAddress} />
        <meta property="business:contact_data:locality" content={companyInfo.address.addressLocality} />
        <meta property="business:contact_data:region" content={companyInfo.address.addressRegion} />
        <meta property="business:contact_data:postal_code" content={companyInfo.address.postalCode} />
        <meta property="business:contact_data:country_name" content="India" />
        <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": `Client Reviews & Testimonials - ${companyInfo.name}`,
            "description": `Read authentic client reviews and testimonials about our mobile app development, web applications, UI/UX design, and digital solutions. 100% satisfaction rate with ${totalReviews}+ happy clients.`,
            "url": `${companyInfo.url}/development-reviews`,
            "mainEntity": {
              ...organizationSchema,
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": averageRating.toFixed(1),
                "reviewCount": totalReviews.toString(),
                "bestRating": "5",
                "worstRating": "1"
              }
            }
          })}</script>
      </Helmet>
      <div className="reviews">


      <section className="reviews__hero">
        <div className="reviews__hero-columns">
          <motion.div
            className="reviews__hero-content"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>Client Reviews & Testimonials - Top-Rated Web Development Company</h1>
            <h2 className="reviews__hero-subtitle">
              Authentic Client Testimonials From 50+ Satisfied Customers Worldwide
            </h2>
            <p className="reviews__hero-description">
              Don't just take our word for it. Read real client reviews and testimonials about our 
              web development, mobile app development, UI/UX design, e-commerce solutions, and digital 
              marketing services. With a 100% satisfaction rate and 5-star average rating, see why 
              businesses trust App Developer Pro for their digital transformation needs.
            </p>
          </motion.div>
          <motion.div
            className="reviews__hero-visual"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="reviews__hero-keywords-group">
              <div className="reviews__hero-keywords">
                <h3>Why Choose Our Award-Winning Development Services?</h3>
                <ul>
                  <li>✅ 100% Client Satisfaction Rate</li>
                  <li>✅ 5-Star Average Rating</li>
                  <li>✅ 50+ Successful Projects Delivered</li>
                  <li>✅ Expert Team of Certified Developers</li>
                  <li>✅ On-Time Project Delivery Guaranteed</li>
                  <li>✅ 24/7 Customer Support & Maintenance</li>
                </ul>
              </div>
              <div className="reviews__hero-stats">
                <div className="reviews__hero-stat">
                  <div className="reviews__hero-stat-number">{averageRating.toFixed(1)}</div>
                  <div className="reviews__hero-stat-stars">
                    {renderStars(Math.round(averageRating))}
                  </div>
                  <div className="reviews__hero-stat-label">Average Rating</div>
                </div>
                <div className="reviews__hero-stat">
                  <div className="reviews__hero-stat-number">{totalReviews}+</div>
                  <div className="reviews__hero-stat-label">Happy Clients</div>
                </div>
                <div className="reviews__hero-stat">
                  <div className="reviews__hero-stat-number">100%</div>
                  <div className="reviews__hero-stat-label">Satisfaction Rate</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Client Reviews Grid - Moved to top */}
      <section className="reviews__grid">
        <div className="reviews__container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666', fontSize: '1.1rem' }}>
              Loading reviews...
            </div>
          ) : reviews.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666', fontSize: '1.1rem' }}>
              No reviews available yet.
            </div>
          ) : (
            reviews.map((review, index) => (
            <motion.div
              key={review.id}
              className="reviews__card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              <div className="reviews__card-header">
                <div className="reviews__card-quote">
                  <FaQuoteLeft />
                </div>
                <div className="reviews__card-rating">
                  {renderStars(review.rating)}
                </div>
              </div>
              
              <div className="reviews__card-content">
                <p className="reviews__card-text">{review.review}</p>
              </div>

              <div className="reviews__card-footer">
                <div className="reviews__card-author">
                  {review.image ? (
                    <img 
                      src={review.image}
                      alt={review.name}
                      className="reviews__card-avatar"
                    />
                  ) : (
                    <div className="reviews__card-avatar reviews__card-avatar--placeholder">
                      {review.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="reviews__card-author-info">
                    <span className="reviews__card-name">{review.name}</span>
                    <span className="reviews__card-position">{review.position}</span>
                    <span className="reviews__card-company">{review.company}</span>
                  </div>
                </div>
                <div className="reviews__card-meta">
                  <div className="reviews__card-date">
                    <FaCalendarAlt className="reviews__card-icon" />
                    {new Date(review.createdAt).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </div>
                  <button className="reviews__card-helpful">
                    <FaThumbsUp className="reviews__card-icon" />
                    Helpful
                  </button>
                </div>
              </div>
            </motion.div>
          ))
          )}
        </div>
      </section>

      {/* Review Submission Form */}
      <ReviewForm />

      {/* SEO Content Section */}
      <section className="reviews__seo-content">
        <div className="container">
          <article className="reviews__seo-article">
            <h2>Why Client Reviews Matter for Your Business Decision</h2>
            <p>
              When choosing a web development company or mobile app development partner, client reviews 
              and testimonials provide invaluable insights into the quality of service, project delivery, 
              and customer satisfaction. Our collection of authentic client reviews showcases real 
              experiences from businesses who have transformed their digital presence with our expert 
              development services.
            </p>
            
            <h3>Our Proven Track Record in Web Development Excellence</h3>
            <p>
              With over 50 successfully completed projects, App Developer Pro has established itself 
              as a leading web development company specializing in custom software solutions, 
              responsive web design, e-commerce development, and mobile application development. 
              Our client testimonials reflect our commitment to delivering high-quality, scalable, 
              and user-friendly digital solutions.
            </p>

            <h4>Services That Consistently Earn 5-Star Reviews:</h4>
            <ul className="reviews__services-list">
              <li><span role="img" aria-label="web">💻</span><div><strong>Custom Web Development</strong><span>Responsive, SEO-optimized websites that drive results</span></div></li>
              <li><span role="img" aria-label="webapp">🖥️</span><div><strong>Web Application Development</strong><span>Custom web apps for business automation, portals, and SaaS platforms</span></div></li>
              <li><span role="img" aria-label="mobile">📱</span><div><strong>Mobile App Development</strong><span>iOS and Android apps with seamless user experience</span></div></li>
              <li><span role="img" aria-label="uiux">🎨</span><div><strong>UI/UX Design</strong><span>User-centered design that converts visitors into customers</span></div></li>
              <li><span role="img" aria-label="ecommerce">🛒</span><div><strong>E-commerce Solutions</strong><span>Powerful online stores that boost sales and revenue</span></div></li>
              <li><span role="img" aria-label="marketing">📢</span><div><strong>Digital Marketing</strong><span>SEO, PPC, and social media strategies that generate leads</span></div></li>
              <li><span role="img" aria-label="cloud">☁️</span><div><strong>Cloud Solutions</strong><span>Scalable cloud infrastructure for modern businesses</span></div></li>
              <li><span role="img" aria-label="devops">⚙️</span><div><strong>DevOps Services</strong><span>Streamlined deployment and continuous integration</span></div></li>
            </ul>

            <h4>What Makes Our Client Reviews Authentic and Trustworthy?</h4>
            <p>
              Every testimonial featured on this page comes from real clients who have worked directly 
              with our development team. We believe in transparency and authenticity, which is why we 
              include detailed project information, client names, and specific results achieved. Our 
              reviews cover various industries and project types, giving you a comprehensive view of 
              our capabilities and expertise.
            </p>

            <h5>Industries We Serve with Excellence:</h5>
            <div className="reviews__industries">
              <span className="industry-tag">Technology Startups</span>
              <span className="industry-tag">E-commerce Businesses</span>
              <span className="industry-tag">Healthcare Companies</span>
              <span className="industry-tag">Financial Services</span>
              <span className="industry-tag">Education Sector</span>
              <span className="industry-tag">Real Estate</span>
              <span className="industry-tag">Manufacturing</span>
              <span className="industry-tag">Non-Profit Organizations</span>
            </div>

            <h6>Ready to Join Our Growing List of Satisfied Clients?</h6>
            <p className="reviews__cta-text">
              Contact App Developer Pro today for a free consultation and project quote. Let us help 
              you achieve the same success and satisfaction that our existing clients experience. 
              Join the ranks of businesses who trust us with their digital transformation journey.
            </p>
          </article>
        </div>
      </section>

      {/* Additional SEO Content */}
      <section className="reviews__additional-content">
        <div className="container">
          <h2>Frequently Asked Questions About Our Development Services</h2>
          
          <div className="reviews__faq">
            <h3>How do you ensure quality in web development projects?</h3>
            <p>
              Our quality assurance process includes thorough testing, code reviews, performance 
              optimization, and client feedback integration. Every project undergoes multiple 
              testing phases to ensure bug-free delivery and optimal user experience.
            </p>

            <h3>What makes your mobile app development services unique?</h3>
            <p>
              We specialize in creating native and cross-platform mobile applications with focus 
              on user experience, performance, and scalability. Our apps are designed to meet 
              App Store and Google Play guidelines while delivering exceptional functionality.
            </p>

            <h3>How long does a typical web development project take?</h3>
            <p>
              Project timelines vary based on complexity and requirements. Simple websites typically 
              take 2-4 weeks, while complex web applications may require 2-6 months. We provide 
              detailed timelines during the consultation phase and maintain transparent communication 
              throughout the development process.
            </p>

            <h3>Do you provide ongoing support and maintenance?</h3>
            <p>
              Yes, we offer comprehensive support and maintenance services including regular updates, 
              security patches, performance monitoring, and feature enhancements. Our support team 
              is available 24/7 to address any issues and ensure your digital assets remain secure 
              and up-to-date.
            </p>
          </div>

          <h2>Client Success Stories and Measurable Results</h2>
          <p>
            Beyond testimonials, we're proud to share the measurable impact of our development services. 
            From startups achieving rapid growth to established businesses modernizing their digital 
            infrastructure, our portfolio demonstrates consistent results across industries and project types.
          </p>

          <h3>Key Performance Indicators From Our Client Projects:</h3>
          <ul className="reviews__kpis">
            <li><strong>300% Average Increase</strong> in website traffic for our SEO-optimized sites</li>
            <li><strong>250% Boost in Conversions</strong> through improved UX/UI design</li>
            <li><strong>99.9% Uptime</strong> for all hosted applications and websites</li>
            <li><strong>40% Reduction</strong> in operational costs through cloud migration</li>
            <li><strong>100% On-Time Delivery</strong> rate for all completed projects</li>
            <li><strong>24-Hour Response Time</strong> for all support requests</li>
          </ul>
        </div>
      </section>

      <section className="reviews__cta">
        <div className="reviews__cta-container">
          {/* Left Column */}
          <motion.div
            className="reviews__cta-left"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Ready to Start Your Digital Transformation Journey?</h2>
            <h3>Get Your Free Consultation and Project Quote Today</h3>
            <p>
              Join our growing list of satisfied clients and achieve the same success showcased in 
              these testimonials. Our expert team is ready to transform your digital vision into 
              reality with cutting-edge web development, mobile app development, and digital marketing solutions.
            </p>
          </motion.div>

          {/* Right Column */}
          <motion.div
            className="reviews__cta-right"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="reviews__cta-benefits">
              <ul>
                <li>✅ Free Initial Consultation & Strategy Session</li>
                <li>✅ Detailed Project Roadmap & Timeline</li>
                <li>✅ Transparent Pricing - No Hidden Costs</li>
                <li>✅ 100% Satisfaction Guarantee</li>
                <li>✅ 24/7 Ongoing Support & Maintenance</li>
              </ul>
            </div>
          </motion.div>
        </div>

        {/* CTA Button and Contact - Full Width Below */}
        <motion.div
          className="reviews__cta-actions"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <motion.a
            href="/contact"
            className="reviews__cta-btn"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Start Your Project Today - Get Free Quote
          </motion.a>
          <p className="reviews__cta-contact">
            <strong>Call Now:</strong> <a href="tel:+19826360188">+1 (982) 636-0188</a> | 
            <strong> Email:</strong> <a href="mailto:info@appdeveloperpro.com">info@appdeveloperpro.com</a>
          </p>
        </motion.div>
      </section>
      </div>
    </>
  );
};

export default Reviews;
